<template>
  <div class="injury-heatmap" :data-view-mode="viewMode">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>
          Loading
          {{
            viewMode === "day"
              ? "daily"
              : viewMode === "week"
                ? "weekly"
                : "monthly"
          }}
          data...
        </p>
      </div>
    </div>

    <!-- Daily View Range Controls -->
    <div v-if="viewMode === 'day'" class="daily-controls">
      <div class="range-info">
        <span class="range-label">Showing:</span>
        <span class="range-value">{{ dailyRangeText }}</span>
        <button @click="expandDailyRange" class="expand-btn" :disabled="isFullRangeShown">
          {{ isFullRangeShown ? "Full Range Shown" : "Show More Days" }}
        </button>
      </div>
    </div>

    <!-- Heat Map Grid -->
    <div class="heatmap-grid" :style="gridStyles" :class="{ loading: isLoading }">
      <div class="heatmap-header">
        <div class="corner-cell"></div>
        <div v-for="period in timePeriods" :key="period" class="header-cell">
          {{ formatPeriod(period) }}
        </div>
      </div>

      <div v-for="team in teams" :key="team.teamId" class="heatmap-row">
        <div class="row-header">{{ team.teamName }}</div>
        <div v-for="period in timePeriods" :key="`${team.teamId}-${period}`" class="heatmap-cell"
          :style="getCellStyle(team.teamId, period)" @click="handleCellClick(team.teamId, period)"
          :class="{ 'daily-cell': viewMode === 'day' }" :title="getCellTooltip(team.teamId, period)">
          {{ getInjuryCount(team.teamId, period) }}
        </div>
      </div>
    </div>

    <div class="heatmap-legend">
      <span>Low</span>
      <div class="legend-gradient"></div>
      <span>High</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "InjuryHeatMap",
  props: {
    injuries: { type: Array, required: true },
    teams: { type: Array, required: true },
    viewMode: { type: String, default: "month" },
    dateRange: { type: Array, required: true },
  },
  data() {
    return {
      isLoading: false,
      dailyRangeLimit: 90, // Default to 90 days for performance
      maxDailyRange: 365,
      viewChangeTimeout: null,
    };
  },
  computed: {
    timePeriods() {
      return this.generateTimePeriods();
    },
    heatMapData() {
      return this.processHeatMapData();
    },
    maxInjuryCount() {
      const counts = Object.values(this.heatMapData).map((team) =>
        Math.max(...Object.values(team)),
      );
      return Math.max(...counts, 1); // Ensure minimum of 1 to avoid division by zero
    },
    gridStyles() {
      return {
        gridTemplateColumns: `200px repeat(${this.timePeriods.length}, 1fr)`,
      };
    },
    dailyRangeText() {
      if (this.viewMode !== "day") return "";

      try {
        const periodsCount = this.timePeriods ? this.timePeriods.length : 0;
        const maxPossible = this.getMaxPossibleDailyPeriods();

        if (periodsCount >= maxPossible) {
          return `All ${periodsCount} days`;
        }
        return `${periodsCount} of ${maxPossible} days`;
      } catch (error) {
        console.warn("Error calculating daily range text:", error);
        return "Loading...";
      }
    },
    isFullRangeShown() {
      if (this.viewMode !== "day") return false;
      return this.dailyRangeLimit >= this.getMaxPossibleDailyPeriods();
    },
  },
  watch: {
    viewMode: {
      handler(newMode, oldMode) {
        if (newMode !== oldMode) {
          this.handleViewModeChange(newMode);
        }
      },
      immediate: false,
    },
    dateRange: {
      handler() {
        this.handleDataChange();
      },
      deep: true,
    },
  },
  methods: {
    handleViewModeChange(newMode) {
      // Clear any existing timeout
      if (this.viewChangeTimeout) {
        clearTimeout(this.viewChangeTimeout);
      }

      // Show loading immediately for daily view
      if (newMode === "day") {
        this.isLoading = true;
        this.dailyRangeLimit = 90; // Reset to default
      }

      // Debounce the actual processing
      this.viewChangeTimeout = setTimeout(() => {
        this.processViewChange();
      }, 50);
    },

    handleDataChange() {
      if (this.viewMode === "day") {
        this.isLoading = true;
        this.$nextTick(() => {
          setTimeout(() => {
            this.isLoading = false;
          }, 100);
        });
      }
    },

    processViewChange() {
      this.$nextTick(() => {
        setTimeout(
          () => {
            this.isLoading = false;
          },
          this.viewMode === "day" ? 200 : 50,
        );
      });
    },

    processHeatMapData() {
      const data = {};

      this.teams.forEach((team) => {
        data[team.teamId] = {};
        this.timePeriods.forEach((period) => {
          data[team.teamId][period] = 0;
        });
      });

      this.injuries.forEach((injury) => {
        const period = this.getPeriodFromDate(injury.created_date);
        if (data[injury.teamId] && data[injury.teamId][period] !== undefined) {
          data[injury.teamId][period]++;
        }
      });

      return data;
    },

    generateTimePeriods() {
      if (!this.dateRange || this.dateRange.length < 2) {
        return [];
      }

      try {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.warn("Invalid date range provided");
          return [];
        }

        switch (this.viewMode) {
          case "day":
            return this.generateDailyPeriods(startDate, endDate);
          case "week":
            return this.generateWeeklyPeriods(startDate, endDate);
          case "month":
            return this.generateMonthlyPeriods(startDate, endDate);
          default:
            return this.generateMonthlyPeriods(startDate, endDate);
        }
      } catch (error) {
        console.error("Error generating time periods:", error);
        return [];
      }
    },

    generateDailyPeriods(startDate, endDate) {
      const periods = [];
      const currentDate = new Date(startDate);

      // Limit daily periods for performance
      const maxEndDate = new Date(startDate);
      maxEndDate.setDate(maxEndDate.getDate() + this.dailyRangeLimit - 1);

      const effectiveEndDate = maxEndDate < endDate ? maxEndDate : endDate;

      while (currentDate <= effectiveEndDate) {
        periods.push(currentDate.toISOString().split("T")[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return periods;
    },

    getMaxPossibleDailyPeriods() {
      if (!this.dateRange || this.dateRange.length < 2) return 0;

      try {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          return 0;
        }

        const timeDiff = endDate.getTime() - startDate.getTime();
        return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
      } catch (error) {
        console.warn("Error calculating daily periods:", error);
        return 0;
      }
    },

    expandDailyRange() {
      if (this.isFullRangeShown) return;

      this.isLoading = true;
      this.dailyRangeLimit = Math.min(
        this.dailyRangeLimit + 90,
        this.getMaxPossibleDailyPeriods(),
      );

      // Use timeout to show loading state
      this.$nextTick(() => {
        setTimeout(() => {
          this.isLoading = false;
        }, 100);
      });
    },

    generateWeeklyPeriods(startDate, endDate) {
      const periods = [];
      const currentDate = new Date(startDate);

      // Move to start of week (Monday)
      currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 1);

      while (currentDate <= endDate) {
        periods.push(currentDate.toISOString().split("T")[0]);
        currentDate.setDate(currentDate.getDate() + 7);
      }

      return periods;
    },

    generateMonthlyPeriods(startDate, endDate) {
      const periods = [];
      const currentDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        1,
      );
      const endDateMonth = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        1,
      );

      while (currentDate <= endDateMonth) {
        const yearMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
        periods.push(yearMonth);
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      return periods;
    },

    getPeriodFromDate(dateString) {
      const date = new Date(dateString);

      switch (this.viewMode) {
        case "day":
          return date.toISOString().split("T")[0];
        case "week":
          return this.getWeekKey(dateString);
        case "month":
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        default:
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      }
    },

    getWeekKey(dateString) {
      const date = new Date(dateString);
      const startOfWeek = new Date(
        date.setDate(date.getDate() - date.getDay() + 1),
      );
      return startOfWeek.toISOString().split("T")[0];
    },

    formatPeriod(period) {
      switch (this.viewMode) {
        case "day": {
          const date = new Date(period);
          return date.toLocaleDateString("en-GB", {
            month: "short",
            day: "numeric",
          });
        }
        case "week": {
          const weekDate = new Date(period);
          return `${weekDate.toLocaleDateString("en-GB", { month: "short", day: "numeric" })}`;
        }
        case "month": {
          const [year, month] = period.split("-");
          const monthDate = new Date(year, month - 1);
          return monthDate.toLocaleDateString("en-GB", {
            month: "short",
            year: "2-digit",
          });
        }
        default:
          return period;
      }
    },

    getInjuryCount(teamId, period) {
      return this.heatMapData[teamId]?.[period] || 0;
    },

    getCellStyle(teamId, period) {
      const count = this.getInjuryCount(teamId, period);
      const intensity =
        this.maxInjuryCount > 0 ? count / this.maxInjuryCount : 0;

      return {
        backgroundColor: `rgba(220, 53, 69, ${intensity})`,
        color: intensity > 0.5 ? "white" : "black",
        cursor: count > 0 ? "pointer" : "default",
      };
    },

    getCellTooltip(teamId, period) {
      try {
        const count = this.getInjuryCount(teamId, period);
        const team = this.teams.find((t) => t.teamId === teamId);
        const formattedPeriod = this.formatPeriod(period);
        return `${team?.teamName || "Unknown"} - ${formattedPeriod}: ${count} injuries`;
      } catch (error) {
        console.warn("Error generating cell tooltip:", error);
        return "Loading...";
      }
    },

    handleCellClick(teamId, period) {
      const count = this.getInjuryCount(teamId, period);
      if (count > 0) {
        this.$emit("cell-click", {
          teamId,
          period,
          count,
          teamName: this.teams.find((t) => t.teamId === teamId)?.teamName,
          type: "heatmap",
          viewMode: this.viewMode,
        });
      }
    },
  },
  beforeUnmount() {
    // Clean up any pending timeouts
    if (this.viewChangeTimeout) {
      clearTimeout(this.viewChangeTimeout);
    }
  },
};
</script>

<style scoped>
.injury-heatmap {
  padding: 20px;
  position: relative;
}

/* Loading States */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
}

.loading-spinner {
  text-align: center;
  padding: 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-spinner p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

/* Daily Controls */
.daily-controls {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.range-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.range-label {
  font-weight: 600;
  color: #495057;
}

.range-value {
  color: #007bff;
  font-weight: 500;
}

.expand-btn {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.expand-btn:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
}

.expand-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  transform: none;
}

.heatmap-grid {
  display: grid;
  gap: 1px;
  background: #dee2e6;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.heatmap-header {
  display: contents;
}

.corner-cell {
  background: #f8f9fa;
  padding: 12px;
  font-weight: 600;
  text-align: center;
  border-right: 1px solid #dee2e6;
}

.header-cell {
  background: #f8f9fa;
  padding: 12px;
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.heatmap-row {
  display: contents;
}

.row-header {
  background: #f8f9fa;
  padding: 12px;
  font-weight: 600;
  border-right: 1px solid #dee2e6;
  font-size: 0.9rem;
}

.heatmap-cell {
  background: white;
  padding: 12px;
  text-align: center;
  transition: all 0.2s ease;
  font-weight: 500;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.heatmap-cell:hover {
  transform: scale(1.05);
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.heatmap-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  gap: 10px;
  font-size: 0.9rem;
}

.legend-gradient {
  width: 200px;
  height: 20px;
  background: linear-gradient(to right,
      rgba(220, 53, 69, 0.1),
      rgba(220, 53, 69, 1));
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .heatmap-grid {
    font-size: 0.8rem;
  }

  .heatmap-cell {
    padding: 8px;
    min-height: 36px;
  }

  .row-header {
    font-size: 0.8rem;
  }

  .header-cell {
    font-size: 0.8rem;
  }
}

/* Special handling for daily view with many periods */
.injury-heatmap[data-view-mode="day"] .heatmap-grid {
  overflow-x: auto;
  max-width: 100%;
}

.injury-heatmap[data-view-mode="day"] .header-cell {
  min-width: 60px;
  writing-mode: vertical-lr;
  text-orientation: mixed;
  padding: 8px 4px;
}

.injury-heatmap[data-view-mode="day"] .heatmap-cell {
  min-width: 60px;
  padding: 8px 4px;
}

@media (max-width: 480px) {

  .injury-heatmap[data-view-mode="day"] .header-cell,
  .injury-heatmap[data-view-mode="day"] .heatmap-cell {
    min-width: 40px;
    padding: 6px 2px;
  }
}
</style>
